import { useRef } from 'react';
import { Pencil, RotateCcw } from 'lucide-react';
import { slugifyName } from '../lib/slug';

/**
 * A read-only-with-pencil slug input.
 *
 *   - When `value` is `null`, the field shows the slugified `derivedFromName`
 *     and is disabled. The pencil button unlocks editing.
 *   - When `value` is a string, the field is editable. The reset button
 *     pops back to `null` so the field returns to live-derived mode.
 *
 * Callers submit the result of `resolvedValue(value, derivedFromName)` to
 * the API — either the manual override or the auto-generated slug.
 */
export type SlugValue = string | null;

interface Props {
  derivedFromName: string;
  value: SlugValue;
  onChange: (next: SlugValue) => void;
  /** Optional prefix shown inline before the slug, e.g. "invitey.ca/" or "/locations/". */
  prefix?: string;
  /** Placeholder shown when both name and value are empty. */
  placeholder?: string;
}

export function SlugField({ derivedFromName, value, onChange, prefix, placeholder }: Props) {
  const auto = slugifyName(derivedFromName);
  const isManual = value !== null;
  const display = isManual ? value : auto;
  const inputRef = useRef<HTMLInputElement>(null);

  function unlock() {
    onChange(auto);
    // Focus the input after React flushes the disabled→enabled transition.
    queueMicrotask(() => inputRef.current?.focus());
  }

  function reset() {
    onChange(null);
  }

  return (
    <div className="slug-field" data-manual={isManual ? 'true' : 'false'}>
      {prefix && <span className="slug-field-prefix">{prefix}</span>}
      <input
        ref={inputRef}
        value={display}
        disabled={!isManual}
        placeholder={placeholder ?? 'auto-generated'}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
      />
      {isManual ? (
        <button
          type="button"
          className="slug-field-action"
          onClick={reset}
          title="Reset to auto-generated"
          aria-label="Reset to auto-generated"
        >
          <RotateCcw size={14} />
        </button>
      ) : (
        <button
          type="button"
          className="slug-field-action"
          onClick={unlock}
          title="Customize URL"
          aria-label="Customize URL"
        >
          <Pencil size={14} />
        </button>
      )}
    </div>
  );
}

/** What to send to the server: manual value if set, else derived from name. */
export function resolvedSlug(value: SlugValue, derivedFromName: string): string {
  return value !== null ? value : slugifyName(derivedFromName);
}
